import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Native (Capacitor) builds use the PKCE OAuth flow so the deep-link callback
// can exchange the auth code inside the app webview (BUG-034). Web is left on
// the default (implicit) flow — its OAuth behavior must stay unchanged. Read
// the build target directly (not via native.ts) to keep this module free of
// the Capacitor plugin imports that native.ts pulls in.
const IS_NATIVE_BUILD = import.meta.env.VITE_BUILD_TARGET === 'capacitor';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

// Extract project ID from URL for storage key
// URL format: https://{project_id}.supabase.co
const projectId = supabaseUrl?.match(/https:\/\/([^.]+)/)?.[1] || 'afriqahome';
export const STORAGE_KEY = `sb-${projectId}-auth-token`;

// Lazy initialization to avoid errors during SSR/prerender when env vars are missing
let _supabase: SupabaseClient | null = null;

function getSupabaseClient(): SupabaseClient {
  if (_supabase) return _supabase;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables!');
  }

  _supabase = createClient(supabaseUrl, supabaseAnonKey, {
    global: {
      fetch: (...args: Parameters<typeof fetch>) => fetch(...args)
    },
    auth: {
      persistSession: true,
      storageKey: STORAGE_KEY,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Native only: PKCE so the custom-scheme deep-link callback can run
      // exchangeCodeForSession() in the app webview (the code verifier lives in
      // this client's storage). Omitted on web to leave the implicit flow as-is.
      ...(IS_NATIVE_BUILD ? { flowType: 'pkce' as const } : {})
    }
  });

  return _supabase;
}

// Export a proxy that lazily initializes the client
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    return getSupabaseClient()[prop as keyof SupabaseClient];
  }
});